import React, { createContext, useContext, useState, useEffect } from "react";

const TRANSLATIONS = {
  en: {
    // Header / brand
    brand: "ERP Institute",
    subbrand: "SAP Career Advisor",
    cta_admin: "Admin",

    // Landing hero
    hero_kicker: "AI-powered career guidance",
    hero_title: "Find your next role in the SAP world",
    hero_sub: "Upload your CV or answer a few questions. Get a personal career match, learning roadmap and certification path — grounded in real ERP Institute data.",
    cta_start: "Start assessment",
    trust_1: "No account required",
    trust_2: "CV deleted after processing",
    trust_3: "5 minutes",
    feature_1_t: "Deterministic matching",
    feature_1_d: "Careers are chosen by rules against imported job & certification data — not by an AI guess.",
    feature_2_t: "Bilingual EN / DE",
    feature_2_d: "The advisor supports professionals across German-speaking Europe.",
    feature_3_t: "Real employer examples",
    feature_3_d: "See companies that actually hire for each level of your target career.",

    // Assessment
    method_title: "How would you like to be matched?",
    name_question: "How may we address you?",
    name_placeholder: "First name",
    method_cv: "Upload my CV",
    method_cv_d: "Fastest. PDF or DOCX, up to 10 MB. We extract, then delete.",
    method_q: "Answer questions",
    method_q_d: "No CV needed. Takes about 3 minutes.",
    upload_hint: "Drop your CV or click to browse",
    upload_types: "PDF or DOCX · max 10 MB",
    profile_review: "Review your profile",
    profile_desc: "Edit anything that is not correct. Nothing is used until you confirm.",
    field_position: "Current position",
    field_years: "Years of experience",
    field_education: "Education",
    field_industry: "Industry",
    field_tasks: "Professional tasks",
    field_skills: "Detected skills",
    field_sap: "SAP experience",
    field_lang: "Languages",
    field_location: "Location",
    field_goal: "Career goal",
    confirm: "Looks good — continue",
    next: "Next",
    back: "Back",
    processing: "Processing…",

    followup_title: "Three quick questions",
    followup_desc: "These help us fine-tune the match.",
    match_ready: "Your match is ready",
    run_match: "Run match",
    match_title: "Your recommended path",
    match_score: "Match score",
    confidence: "Confidence",

    // Email step / gate
    email_gate: "Unlock full report",
    email_desc: "Enter your email — we'll send you a copy and open the full report.",
    email_send: "Open",
    free_line: "100% free — no credit card required",

    // Report — sections
    report_kicker: "Personal Career Report",
    report_title: "Your SAP Career Report",
    section_profile: "Your professional profile",
    section_role: "Recommended next role",
    section_skills: "Skills analysis",
    strong: "Already strong",
    partial: "Partially covered",
    gap: "Needs development",
    section_roadmap: "Learning roadmap",
    section_cert: "Recommended certification",
    section_course: "Recommended ERP Institute course",
    section_employers: "Potential employer examples",
    section_alt: "Alternative paths",
    section_progression: "Your salary progression",
    salary_footnote: "Upper market bracket, estimates — salaries vary by region and industry.",
    prefilled_hint: "prefilled from your CV",
    email_gate_headline: "Your personal report is almost ready",
    prep_product_title: "Your certification preparation",
    prep_product_prepares: "Prepares for:",
    prep_product_details: "All course details",
    prep_product_courses: "courses",
    prep_product_units: "units",
    step_current: "Your entry",
    step_no_salary: "on request",
    hello: "Hi",
    headline_way_to: "your path to",
    ts_today: "Today",
    ts_1_2: "in 1-2 years",
    ts_2_4: "in 2-4 years",
    ts_4_6: "in 4-6 years",
    ts_later: "later",
    why_fits: "Why this fits",
    disclaimer: "Disclaimer",
    open_course: "Test course for free",
    tasks: "Tasks",
    role_short: "Role",
    industry: "Industry",
    experience: "Experience",
    education: "Education",
    languages: "Languages",
    goal: "Goal",
    next_role: "Next role",
    then: "Then",
    exam_topics: "Exam topics",
    salary_upto: "up to",
    salary_on_request: "on request / tbd",
    next_role_label: "Next role",
    employers_note: "Not current openings — examples only.",
    roadmap_total_prefix: "Your path to certification:",
    roadmap_total_weeks: "weeks",
    course_in_prep: "Course in preparation — landing page not yet live",
    course_in_planning_h: "Course in planning",
    course_in_planning_b: "No live ERP Institute course for this path yet. Book a free consultation and we'll notify you at launch.",
    course_related_paths: "Nearest paths with live courses:",

    // CTAs
    cta_free_start: "Start for free",
    cta_book_call: "Book a call",
    cta_book_ctx: "Talk to us — free consultation",
    booking_modal_title: "Book a free consultation",
    booking_modal_desc: "Share your report with an ERP Institute expert to prepare your call — completely free, no payment info needed.",
    consent_label: "I agree that my extracted profile and generated report may be shared with ERP Institute for the purpose of this consultation. (GDPR)",
    consent_hint: "You can revoke this at any time.",
    booking_continue: "Open booking calendar",
    booking_skip: "Continue without sharing my report",
    booking_no_url: "Booking is not configured yet — please contact us via email.",

    // Alt paths
    alt_click_hint: "Click any path to preview its report",
    alt_reroute_loading: "Reloading recommendation…",

    // Roadmap accordion
    roadmap_expand: "Show details",
    roadmap_collapse: "Hide details",
  },
  de: {
    brand: "ERP Institute",
    subbrand: "SAP Karriere-Berater",
    cta_admin: "Admin",

    hero_kicker: "KI-gestützte Karriereberatung",
    hero_title: "Finde deine nächste Rolle in der SAP-Welt",
    hero_sub: "Lade deinen Lebenslauf hoch oder beantworte ein paar Fragen. Erhalte ein persönliches Karriere-Match, einen Lernpfad und die passende Zertifizierung — auf Basis realer ERP-Institute-Daten.",
    cta_start: "Assessment starten",
    trust_1: "Kein Konto nötig",
    trust_2: "CV wird nach Verarbeitung gelöscht",
    trust_3: "5 Minuten",
    feature_1_t: "Deterministisches Matching",
    feature_1_d: "Karrieren werden über Regeln aus importierten Daten gewählt — nicht per KI-Rateverfahren.",
    feature_2_t: "Zweisprachig DE / EN",
    feature_2_d: "Der Berater unterstützt Fach- und Führungskräfte im DACH-Raum.",
    feature_3_t: "Echte Arbeitgeber-Beispiele",
    feature_3_d: "Sieh Unternehmen, die tatsächlich auf jeder Karrierestufe einstellen.",

    method_title: "Wie möchtest du gematcht werden?",
    name_question: "Wie dürfen wir dich ansprechen?",
    name_placeholder: "Vorname",
    method_cv: "Lebenslauf hochladen",
    method_cv_d: "Am schnellsten. PDF oder DOCX, bis 10 MB. Wir extrahieren und löschen sofort.",
    method_q: "Fragen beantworten",
    method_q_d: "Kein CV nötig. Dauert ca. 3 Minuten.",
    upload_hint: "CV hier ablegen oder klicken",
    upload_types: "PDF oder DOCX · max. 10 MB",
    profile_review: "Prüfe dein Profil",
    profile_desc: "Passe alles an, was nicht stimmt. Nichts wird verwendet, bevor du bestätigst.",
    field_position: "Aktuelle Position",
    field_years: "Berufsjahre",
    field_education: "Ausbildung",
    field_industry: "Branche",
    field_tasks: "Berufliche Aufgaben",
    field_skills: "Erkannte Skills",
    field_sap: "SAP-Erfahrung",
    field_lang: "Sprachen",
    field_location: "Standort",
    field_goal: "Karriereziel",
    confirm: "Passt — weiter",
    next: "Weiter",
    back: "Zurück",
    processing: "Verarbeite…",

    followup_title: "Drei kurze Fragen",
    followup_desc: "Damit wird das Match noch präziser.",
    match_ready: "Dein Match ist bereit",
    run_match: "Match starten",
    match_title: "Dein empfohlener Karrierepfad",
    match_score: "Match-Score",
    confidence: "Konfidenz",

    email_gate: "Vollständigen Report freischalten",
    email_desc: "Gib deine E-Mail ein — wir senden dir eine Kopie und öffnen den kompletten Bericht.",
    email_send: "Öffnen",
    free_line: "100 % kostenlos starten — keine Zahlungsdaten nötig",

    report_kicker: "Persönlicher Karriere-Report",
    report_title: "Dein SAP-Karriere-Report",
    section_profile: "Dein professionelles Profil",
    section_role: "Empfohlene nächste Rolle",
    section_skills: "Skill-Analyse",
    strong: "Bereits stark",
    partial: "Teilweise vorhanden",
    gap: "Zu entwickeln",
    section_roadmap: "Lern-Roadmap",
    section_cert: "Empfohlene Zertifizierung",
    section_course: "Empfohlener ERP-Institute-Kurs",
    section_employers: "Potenzielle Arbeitgeber-Beispiele",
    section_alt: "Alternative Karrierepfade",
    section_progression: "Deine Gehaltsentwicklung",
    salary_footnote: "Obere Marktspanne, Schätzwerte — Gehälter variieren je nach Region und Branche.",
    prefilled_hint: "aus deinem Lebenslauf übernommen",
    email_gate_headline: "Dein persönlicher Report ist fast bereit",
    prep_product_title: "Deine Zertifizierungsvorbereitung",
    prep_product_prepares: "Bereitet vor auf:",
    prep_product_details: "Alle Details zum Kurs",
    prep_product_courses: "Kurse",
    prep_product_units: "Units",
    step_current: "Dein Einstieg",
    step_no_salary: "auf Anfrage",
    hello: "Hallo",
    headline_way_to: "dein Weg zum",
    ts_today: "Heute",
    ts_1_2: "in 1–2 Jahren",
    ts_2_4: "in 2–4 Jahren",
    ts_4_6: "in 4–6 Jahren",
    ts_later: "später",
    why_fits: "Warum das passt",
    disclaimer: "Hinweis",
    open_course: "Kurs kostenlos testen",
    tasks: "Aufgaben",
    role_short: "Rolle",
    industry: "Branche",
    experience: "Erfahrung",
    education: "Ausbildung",
    languages: "Sprachen",
    goal: "Ziel",
    next_role: "Nächste Rolle",
    then: "Danach",
    exam_topics: "Prüfungsthemen",
    salary_upto: "bis zu",
    salary_on_request: "auf Anfrage / tbd",
    next_role_label: "Nächste Rolle",
    employers_note: "Kein aktuelles Stellenangebot – nur Beispiele.",
    roadmap_total_prefix: "Dein Weg zur Zertifizierung:",
    roadmap_total_weeks: "Wochen",
    course_in_prep: "Kurs in Planung – Landing-Seite in Vorbereitung",
    course_in_planning_h: "Kurs in Planung",
    course_in_planning_b: "Für diesen Pfad ist aktuell kein ERP-Institute-Kurs verfügbar. Buche ein kostenloses Beratungsgespräch — wir informieren dich zum Kursstart.",
    course_related_paths: "Verwandte Pfade mit verfügbaren Kursen:",

    cta_free_start: "Kostenlos starten",
    cta_book_call: "Termin buchen",
    cta_book_ctx: "Sprich mit uns — kostenloses Beratungsgespräch",
    booking_modal_title: "Kostenloses Beratungsgespräch buchen",
    booking_modal_desc: "Teile deinen Report mit einem ERP-Institute-Experten, damit wir das Gespräch vorbereiten können — komplett kostenlos, keine Zahlungsdaten nötig.",
    consent_label: "Ich stimme zu, dass mein extrahiertes Profil und der generierte Report an ERP Institute zum Zweck dieses Beratungsgesprächs übermittelt werden dürfen. (DSGVO)",
    consent_hint: "Du kannst dies jederzeit widerrufen.",
    booking_continue: "Kalender öffnen",
    booking_skip: "Ohne Report-Freigabe weiter",
    booking_no_url: "Buchung noch nicht konfiguriert — bitte per E-Mail Kontakt aufnehmen.",

    alt_click_hint: "Klick auf einen Pfad, um den Report dafür zu sehen",
    alt_reroute_loading: "Empfehlung wird neu geladen…",

    roadmap_expand: "Details anzeigen",
    roadmap_collapse: "Details verbergen",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "de");
  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  const t = (k) => TRANSLATIONS[lang][k] ?? TRANSLATIONS.en[k] ?? k;
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

// Expose for admin i18n check
export function getI18nDictionary() {
  return TRANSLATIONS;
}
